import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '@/components/PageHeader';
import EditWorkspaceMembershipModal, {
  type WorkspaceMembershipSavePayload,
} from '@/components/EditWorkspaceMembershipModal';
import RemoveOrganizationMemberModal from '@/components/RemoveOrganizationMemberModal';
import SourceAssignmentPicker, { normalizeSourceId } from '@/components/SourceAssignmentPicker';
import {
  organizationsService,
  type OrgInvitation,
  type OrgMember,
} from '@/api/services/organizations';
import { workspaceSourcesService, workspaceSourcesToPickerSources } from '@/api/services/workspaceSources';
import { workspaceUsersService } from '@/api/services/workspaceUsers';
import { formatApiError } from '@/utils/formatApiError';
import {
  CUSTOMER_WORKSPACE_ROLE_OPTIONS,
  platformStaffBadgeLabel,
  workspaceRoleLabel,
  canEditWorkspaceMembershipRole,
  normalizeWorkspaceRole,
} from '@/utils/workspaceRoles';
import { renderMemberEffectivePlan } from '@/utils/effectivePlanDisplay';
import { isPlatformStaffRole } from '@/utils/teamRoles';
import { useWorkspace } from '@/context/WorkspaceContext';
import { useMe } from '@/context/MeContext';
import { useToast } from '@/components/Toast';
import { DataTableScrollArea } from '@/components/table';
import { ToggleField } from '@/components/ui';
import type { Source } from '@/types';

/** Email invitations are secondary; hide until outbound email is configured for SaaS onboarding. */
const WORKSPACE_EMAIL_INVITES_ENABLED = false;

function membershipStatusLabel(status?: string | null): string {
  const value = (status ?? 'active').trim().toLowerCase();
  if (value === 'disabled') return 'Disabled';
  return 'Active';
}

function isWorkspaceAdminRole(role: string): boolean {
  return normalizeWorkspaceRole(role) === 'workspace_admin';
}

function defaultSourceIdsForRole(
  role: string,
  enabledSourceIds: string[],
  defaultMemberSourceIds: string[],
): Set<string> {
  if (isWorkspaceAdminRole(role)) {
    return new Set(enabledSourceIds.map(normalizeSourceId));
  }
  const defaults = defaultMemberSourceIds.length > 0 ? defaultMemberSourceIds : enabledSourceIds;
  return new Set(defaults.map(normalizeSourceId));
}

function renderMemberSources(member: OrgMember): React.ReactNode {
  const access = member.sourceAccess;
  if (!access) return '—';
  const { assignedCount, workspaceEnabledCount, sources } = access;
  if (workspaceEnabledCount > 0 && assignedCount >= workspaceEnabledCount) {
    return `All ${workspaceEnabledCount} sources`;
  }
  if (assignedCount === 0) {
    return 'No sources assigned';
  }
  const names = (sources ?? []).map((source) => source.displayName || source.name).join(', ');
  return <span title={names}>{`${assignedCount} / ${workspaceEnabledCount}`}</span>;
}

export default function WorkspaceUsersPage() {
  const { toast } = useToast();
  const { me } = useMe();
  const { workspaceVersion, isPersonalMode, activeWorkspaceName } = useWorkspace();
  const canManageMembers = me?.permissions.canManageWorkspaceMembers ?? false;
  const canManageAccount = me?.permissions.canManageUsers ?? false;
  const readOnly = me?.permissions.readOnly ?? false;
  const [workspaceName, setWorkspaceName] = useState(activeWorkspaceName ?? 'Workspace');
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [invitations, setInvitations] = useState<OrgInvitation[]>([]);
  const [addEmail, setAddEmail] = useState('');
  const [addName, setAddName] = useState('');
  const [addPassword, setAddPassword] = useState('');
  const [addRole, setAddRole] = useState('subscriber');
  const [addEnabled, setAddEnabled] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('subscriber');
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [editMember, setEditMember] = useState<OrgMember | null>(null);
  const [removeMember, setRemoveMember] = useState<OrgMember | null>(null);
  const [workspaceSources, setWorkspaceSources] = useState<Source[]>([]);
  const [enabledSourceIds, setEnabledSourceIds] = useState<string[]>([]);
  const [defaultMemberSourceIds, setDefaultMemberSourceIds] = useState<string[]>([]);
  const [addSelectedSourceIds, setAddSelectedSourceIds] = useState<Set<string>>(new Set());
  const [loadingAddSources, setLoadingAddSources] = useState(false);

  const enabledWorkspaceSources = useMemo(
    () => workspaceSources.filter((source) => source.is_enabled),
    [workspaceSources],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setMembers([]);
    setInvitations([]);
    setShowAddForm(false);
    setShowInviteForm(false);
    setEditMember(null);
    setRemoveMember(null);
    try {
      const workspaceUsers = await workspaceUsersService.list();
      setWorkspaceName(workspaceUsers.workspaceName);
      setMembers(workspaceUsers.items);
      if (WORKSPACE_EMAIL_INVITES_ENABLED && workspaceUsers.workspaceMode === 'organization') {
        const invites = await organizationsService.invitations();
        setInvitations(invites.items.filter((x) => x.status === 'pending'));
      } else {
        setInvitations([]);
      }
      setError(null);
    } catch (e) {
      const msg = formatApiError(e);
      if (readOnly && /admin access required|permission denied|not allowed/i.test(msg)) {
        setError(null);
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  }, [readOnly]);

  useEffect(() => {
    load().catch(() => {});
  }, [load, workspaceVersion]);

  useEffect(() => {
    if (!showAddForm || !canManageMembers || isPersonalMode) return;
    let cancelled = false;
    async function loadAddSources() {
      setLoadingAddSources(true);
      try {
        const settings = await workspaceSourcesService.getSettings();
        if (cancelled) return;
        const enabledIds = settings.enabledSourceIds;
        setEnabledSourceIds(enabledIds);
        setDefaultMemberSourceIds(settings.defaultMemberSourceIds);
        setWorkspaceSources(
          workspaceSourcesToPickerSources(
            settings.availableSources.filter((source) => enabledIds.includes(source.id)),
          ),
        );
        setAddSelectedSourceIds(defaultSourceIdsForRole(addRole, enabledIds, settings.defaultMemberSourceIds));
      } catch (err) {
        if (!cancelled) toast(formatApiError(err), 'error');
      } finally {
        if (!cancelled) setLoadingAddSources(false);
      }
    }
    void loadAddSources();
    return () => {
      cancelled = true;
    };
  }, [showAddForm, canManageMembers, isPersonalMode, addRole]);

  useEffect(() => {
    if (!showAddForm) return;
    setAddSelectedSourceIds(defaultSourceIdsForRole(addRole, enabledSourceIds, defaultMemberSourceIds));
  }, [addRole, defaultMemberSourceIds, enabledSourceIds, showAddForm]);

  async function addWorkspaceUser(e: React.FormEvent) {
    e.preventDefault();
    setAdding(true);
    try {
      const selectedIds = enabledWorkspaceSources
        .filter((source) => addSelectedSourceIds.has(normalizeSourceId(source.id)))
        .map((source) => source.id);
      await workspaceUsersService.create({
        email: addEmail.trim(),
        displayName: addName.trim() || undefined,
        workspaceRole: addRole,
        password: addPassword.trim() || undefined,
        enabled: addEnabled,
        sourceIds: isWorkspaceAdminRole(addRole)
          ? undefined
          : selectedIds.length
            ? selectedIds
            : undefined,
      });
      toast('Workspace user added', 'success');
      setAddEmail('');
      setAddName('');
      setAddPassword('');
      setAddRole('subscriber');
      setAddEnabled(true);
      setShowAddForm(false);
      await load();
    } catch (err) {
      toast(formatApiError(err), 'error');
    } finally {
      setAdding(false);
    }
  }

  async function sendInvite(e: React.FormEvent) {
    e.preventDefault();
    try {
      await organizationsService.invite(inviteEmail.trim(), inviteRole);
      toast('Invitation sent', 'success');
      setInviteEmail('');
      setInviteRole('subscriber');
      setShowInviteForm(false);
      await load();
    } catch (err) {
      toast(formatApiError(err), 'error');
    }
  }

  async function saveMembership(payload: WorkspaceMembershipSavePayload) {
    if (!editMember) return;
    await organizationsService.updateMember(editMember.id, payload);
    toast('Workspace membership updated', 'success');
    setEditMember(null);
    await load();
  }

  async function confirmRemove() {
    if (!removeMember) return;
    await organizationsService.removeMember(removeMember.id);
    toast('Member removed from workspace', 'success');
    setRemoveMember(null);
    setEditMember(null);
    await load();
  }

  async function revokeInvite(inv: OrgInvitation) {
    try {
      await organizationsService.revokeInvitation(inv.id);
      toast('Invitation revoked', 'success');
      await load();
    } catch (err) {
      toast(formatApiError(err), 'error');
    }
  }

  if (loading) return <div className="panel">Loading users…</div>;

  const subtitle = isPersonalMode
    ? `Members of ${workspaceName}. Personal workspaces include only your account.`
    : `Members of ${workspaceName}. One login account can belong to multiple workspaces; this list shows only the current workspace.`;

  return (
    <div>
      <PageHeader title="Users" subtitle={subtitle} />
      {error ? <div className="alert alert-error">{error}</div> : null}

      {!isPersonalMode && canManageMembers ? (
        <section className="panel">
          <div className="table-action-bar">
            <button type="button" className="btn btn-primary" onClick={() => setShowAddForm((value) => !value)}>
              Add workspace user
            </button>
            {WORKSPACE_EMAIL_INVITES_ENABLED ? (
              <button type="button" className="btn" onClick={() => setShowInviteForm((value) => !value)}>
                Invite by email
              </button>
            ) : null}
          </div>
        </section>
      ) : !isPersonalMode ? (
        <section className="panel">
          <p className="muted">You have read-only access to workspace users.</p>
        </section>
      ) : null}

      {!isPersonalMode && canManageMembers && showAddForm ? (
        <section className="panel">
          <h2>Add workspace user</h2>
          <p className="muted">
            Creates an account if needed, or adds an existing account to this workspace only. The same email can belong
            to other organizations with a different workspace role.
          </p>
          <form className="workspace-user-form" onSubmit={addWorkspaceUser}>
            <div className="formGrid">
              <label>
                Email
                <input
                  type="email"
                  required
                  placeholder="user@example.com"
                  value={addEmail}
                  onChange={(e) => setAddEmail(e.target.value)}
                />
              </label>
              <label>
                Name
                <input
                  type="text"
                  placeholder="Optional"
                  value={addName}
                  onChange={(e) => setAddName(e.target.value)}
                />
              </label>
              <label>
                Initial password
                <input
                  type="password"
                  placeholder="Optional"
                  autoComplete="new-password"
                  value={addPassword}
                  onChange={(e) => setAddPassword(e.target.value)}
                />
                <span className="admin-form-field__hint">Optional. Leave blank to generate one.</span>
              </label>
              <label>
                Workspace role
                <select value={addRole} onChange={(e) => setAddRole(e.target.value)} aria-label="Workspace role">
                  {CUSTOMER_WORKSPACE_ROLE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="toggleRow">
              <ToggleField checked={addEnabled} onChange={setAddEnabled} label="Account enabled" />
            </div>
            <section className="workspace-user-form__sources">
              <h3>Member source access</h3>
              <p className="muted">Sources this member can use when creating searches.</p>
              {loadingAddSources ? (
                <p className="muted">Loading workspace sources…</p>
              ) : enabledWorkspaceSources.length === 0 ? (
                <p className="muted">
                  No workspace sources enabled yet. Enable sources in Workspace settings.
                </p>
              ) : isWorkspaceAdminRole(addRole) ? (
                <p className="muted">
                  Workspace admins can use all {enabledWorkspaceSources.length} workspace enabled sources.
                </p>
              ) : (
                <SourceAssignmentPicker
                  sources={enabledWorkspaceSources}
                  selected={addSelectedSourceIds}
                  onToggle={(sourceId) => {
                    setAddSelectedSourceIds((prev) => {
                      const next = new Set(prev);
                      const key = normalizeSourceId(sourceId);
                      if (next.has(key)) next.delete(key);
                      else next.add(key);
                      return next;
                    });
                  }}
                  onSelectAll={() =>
                    setAddSelectedSourceIds(
                      new Set(enabledWorkspaceSources.map((source) => normalizeSourceId(source.id))),
                    )
                  }
                  onClearAll={() => setAddSelectedSourceIds(new Set())}
                  legend="Assigned sources"
                  hideLegend
                  helperText={`${addSelectedSourceIds.size} of ${enabledWorkspaceSources.length} workspace sources`}
                  summaryFormat="fraction"
                  groupByCategory
                />
              )}
              {!isWorkspaceAdminRole(addRole) && enabledWorkspaceSources.length > 0 ? (
                <div className="table-action-bar">
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={() =>
                      setAddSelectedSourceIds(defaultSourceIdsForRole(addRole, enabledSourceIds, defaultMemberSourceIds))
                    }
                  >
                    Use workspace defaults
                  </button>
                </div>
              ) : null}
            </section>
            <div className="formActions">
              <button type="submit" className="btn btn-primary" disabled={adding || loadingAddSources}>
                {adding ? 'Adding…' : 'Add user'}
              </button>
              <button type="button" className="btn" onClick={() => setShowAddForm(false)}>
                Cancel
              </button>
            </div>
          </form>
        </section>
      ) : null}

      {WORKSPACE_EMAIL_INVITES_ENABLED && !isPersonalMode && canManageMembers && showInviteForm ? (
        <section className="panel">
          <h2>Invite by email</h2>
          <p className="muted">Sends a pending invitation. Membership becomes active after acceptance.</p>
          <form className="form-inline" onSubmit={sendInvite}>
            <input
              type="email"
              required
              placeholder="Email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
            />
            <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)} aria-label="Workspace role">
              {CUSTOMER_WORKSPACE_ROLE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <button type="submit" className="btn">
              Send invitation
            </button>
          </form>
        </section>
      ) : null}

      <section className="panel">
        <h2>{isPersonalMode ? 'Personal workspace user' : 'Workspace members'}</h2>
        <div className="workspace-users-cards-wrap">
          <div className="mobile-card-list">
            {members.length === 0 ? (
              <p className="muted">No users in this workspace yet.</p>
            ) : (
              members.map((m) => {
                const staffMember = m.platformRole ? isPlatformStaffRole(m.platformRole) : false;
                const membershipEditable = !isPersonalMode && canManageMembers && canEditWorkspaceMembershipRole(m);
                const roleLabel = workspaceRoleLabel(m.membershipRole, {
                  platformRole: m.platformRole,
                  membershipRoleLabel: m.membershipRoleLabel,
                });
                const planDisplay = staffMember ? null : renderMemberEffectivePlan(m.effectivePlan);
                return (
                  <article key={m.id} className="mobile-card">
                    <h3 className="mobile-card__title">{m.name ?? m.email ?? 'Member'}</h3>
                    <div className="mobile-card__row">
                      <span className="muted">Email</span>
                      <span>{m.email ?? '—'}</span>
                    </div>
                    <div className="mobile-card__row">
                      <span className="muted">Role</span>
                      <span>{roleLabel}</span>
                    </div>
                    <div className="mobile-card__row">
                      <span className="muted">Status</span>
                      <span>{membershipStatusLabel(m.status)}</span>
                    </div>
                    {planDisplay ? (
                      <div className="mobile-card__row">
                        <span className="muted">Plan</span>
                        <span>{planDisplay.label}</span>
                      </div>
                    ) : null}
                    <div className="mobile-card__row">
                      <span className="muted">Sources</span>
                      <span>{renderMemberSources(m)}</span>
                    </div>
                    {!isPersonalMode && canManageMembers && membershipEditable ? (
                      <div className="mobile-card__actions">
                        <button type="button" className="btn btn-sm" onClick={() => setEditMember(m)}>
                          Edit membership
                        </button>
                        <button type="button" className="btn btn-sm btn-danger" onClick={() => setRemoveMember(m)}>
                          Remove
                        </button>
                      </div>
                    ) : null}
                  </article>
                );
              })
            )}
          </div>
        </div>
        <div className="workspace-users-table-wrap">
        <DataTableScrollArea>
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Workspace role</th>
                <th>Status</th>
                <th>Plan</th>
                <th>Sources</th>
                {!isPersonalMode ? <th>Joined</th> : null}
                {!isPersonalMode && canManageMembers ? <th>Actions</th> : null}
              </tr>
            </thead>
            <tbody>
              {members.length === 0 ? (
                <tr>
                  <td colSpan={isPersonalMode ? 6 : canManageMembers ? 8 : 7} className="muted">
                    No users in this workspace yet.
                  </td>
                </tr>
              ) : (
                members.map((m) => {
                  const staffMember = m.platformRole ? isPlatformStaffRole(m.platformRole) : false;
                  const membershipEditable = !isPersonalMode && canManageMembers && canEditWorkspaceMembershipRole(m);
                  const roleLabel = workspaceRoleLabel(m.membershipRole, {
                    platformRole: m.platformRole,
                    membershipRoleLabel: m.membershipRoleLabel,
                  });
                  return (
                    <tr key={m.id}>
                      <td>
                        {m.name ?? '—'}
                        {staffMember ? (
                          <span className="badge badge--platform-staff" title="Also has a Scoutly staff platform role">
                            {' '}
                            {platformStaffBadgeLabel(m.platformRole)}
                          </span>
                        ) : null}
                      </td>
                      <td>{m.email ?? '—'}</td>
                      <td>{roleLabel}</td>
                      <td>{membershipStatusLabel(m.status)}</td>
                      <td>
                        {staffMember ? (
                          '—'
                        ) : (
                          (() => {
                            const planDisplay = renderMemberEffectivePlan(m.effectivePlan);
                            return (
                              <div>
                                <div>{planDisplay.label}</div>
                                {planDisplay.usage ? (
                                  <div className="muted">{planDisplay.usage}</div>
                                ) : null}
                              </div>
                            );
                          })()
                        )}
                      </td>
                      <td>{renderMemberSources(m)}</td>
                      {!isPersonalMode ? (
                        <td>{m.joinedAt ? new Date(m.joinedAt).toLocaleDateString() : '—'}</td>
                      ) : null}
                      {!isPersonalMode && canManageMembers ? (
                        <td>
                          <div className="table-action-bar">
                            {membershipEditable ? (
                              <>
                                <button
                                  type="button"
                                  className="btn btn-sm"
                                  onClick={() => setEditMember(m)}
                                >
                                  Edit membership
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-danger"
                                  onClick={() => setRemoveMember(m)}
                                >
                                  Remove
                                </button>
                              </>
                            ) : (
                              <span className="badge">Owner</span>
                            )}
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </DataTableScrollArea>
        </div>
      </section>

      {WORKSPACE_EMAIL_INVITES_ENABLED && !isPersonalMode && canManageMembers && invitations.length > 0 ? (
        <section className="panel">
          <h2>Pending invitations</h2>
          <ul>
            {invitations.map((inv) => (
              <li key={inv.id}>
                {inv.email} · {workspaceRoleLabel(inv.membershipRole)} · expires{' '}
                {inv.expiresAt ? new Date(inv.expiresAt).toLocaleDateString() : '—'}
                <button type="button" className="btn btn-sm" onClick={() => revokeInvite(inv)}>
                  Revoke
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {editMember ? (
        <EditWorkspaceMembershipModal
          member={editMember}
          workspaceName={workspaceName}
          canManageAccount={canManageAccount}
          onClose={() => setEditMember(null)}
          onSave={saveMembership}
          onRemove={
            canEditWorkspaceMembershipRole(editMember)
              ? () => {
                  setRemoveMember(editMember);
                }
              : undefined
          }
        />
      ) : null}

      {removeMember ? (
        <RemoveOrganizationMemberModal
          member={removeMember}
          organizationName={workspaceName}
          onClose={() => setRemoveMember(null)}
          onConfirm={confirmRemove}
        />
      ) : null}
    </div>
  );
}
